import { useState } from 'react';

import { api } from '../api.ts';
import { ft } from '../formazas.ts';

/**
 * Partnertörzs összevetése egy biztonsági mentéssel. (2026-10-09)
 *
 * „Valami kavarodás lett a törzsadatokban" — egy partner neve másik partner
 * nevére változott. A teljes visszaállítás ilyenkor nem jó: az a napok, az
 * okmányok és a számlák utolsó napjait is visszavinné.
 *
 * Ez csak az **azonosító adatokat** (név, cégnév, adószám, székhely, cím,
 * kód) veti össze egy mentéssel, és csak azt írja vissza, amit kijelölnek.
 * Az árakhoz nem nyúl: az menet közben változik.
 */

interface Mezo {
  mezo: string;
  cimke: string;
  most: string | null;
  mentesben: string | null;
}

interface Sor {
  etterem_id: number;
  nev_most: string;
  nev_mentesben: string;
  mezok: Mezo[];
}

interface Osszevetes {
  mentes: string;
  mentes_partnerek: number;
  mostani_partnerek: number;
  eltero: Sor[];
  eltunt: Array<{ etterem_id: number; nev: string }>;
  uj: Array<{ etterem_id: number; nev: string }>;
}

export function TorzsOsszevetes({
  mentesNev,
  onKesz,
  onMegse,
}: {
  mentesNev: string;
  onKesz: (uzenet: string) => void;
  onMegse: () => void;
}) {
  const [adat, setAdat] = useState<Osszevetes | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);
  /** Melyik partner melyik mezője legyen visszaírva — „id:mezo" kulccsal. */
  const [kijelolt, setKijelolt] = useState<Set<string>>(new Set());

  async function betolt() {
    setMunkaban(true);
    try {
      const v = await api.get<Osszevetes>(
        `/api/export/torzs-osszevetes?nev=${encodeURIComponent(mentesNev)}`,
      );
      setAdat(v);
      /* Alapból a névcserék vannak kijelölve: azokért nyúl ide az ember. */
      setKijelolt(
        new Set(
          v.eltero.flatMap((s) =>
            s.mezok.filter((m) => m.mezo === 'nev').map((m) => `${s.etterem_id}:${m.mezo}`),
          ),
        ),
      );
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az összevetés nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  async function visszair() {
    if (kijelolt.size === 0 || !adat) return;
    setMunkaban(true);
    try {
      const csoport = new Map<number, string[]>();
      for (const k of kijelolt) {
        const [id, mezo] = k.split(':');
        const szam = Number(id);
        csoport.set(szam, [...(csoport.get(szam) ?? []), mezo!]);
      }
      const v = await api.post<{ partnerek: number; mezok: number }>(
        '/api/export/torzs-visszairas',
        {
          nev: mentesNev,
          kijelolt: [...csoport.entries()].map(([etterem_id, mezok]) => ({ etterem_id, mezok })),
        },
      );
      onKesz(`${v.partnerek} partner ${v.mezok} adata került vissza a mentésből.`);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A visszaírás nem sikerült.');
    } finally {
      setMunkaban(false);
    }
  }

  const valt = (kulcs: string) =>
    setKijelolt((e) => {
      const uj = new Set(e);
      if (uj.has(kulcs)) uj.delete(kulcs);
      else uj.add(kulcs);
      return uj;
    });

  if (!adat) {
    return (
      <div className="uzenet flag">
        <b>Összevetés a(z) {mentesNev} mentéssel.</b> Megnézi, melyik partner azonosító adata tér
        el a mentéstől — nevet, cégnevet, adószámot, székhelyet, címet és kódot. Nem változtat
        semmit, amíg nem jelölöd ki, mit kérsz vissza. Az árakhoz nem nyúl.
        {hiba && <div className="uzenet bad">{hiba}</div>}
        <div className="btns" style={{ marginTop: 8 }}>
          <button className="btn gold" disabled={munkaban} onClick={() => void betolt()}>
            {munkaban ? 'Összevetés…' : 'Összevetés'}
          </button>
          <button className="btn sm" onClick={onMegse}>
            Mégsem
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="uzenet flag">
      <b>
        {adat.mentes}: {ft(adat.eltero.length)} partner adata tér el
      </b>{' '}
      (a mentésben {ft(adat.mentes_partnerek)} partner volt, most {ft(adat.mostani_partnerek)} van).
      {adat.eltunt.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {ft(adat.eltunt.length)} partner a mentésben megvan, most nincs:{' '}
          {adat.eltunt.map((e) => e.nev).join(', ')}
        </div>
      )}
      {adat.uj.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {ft(adat.uj.length)} partner azóta került fel — azokhoz nincs mit visszaírni.
        </div>
      )}
      {hiba && <div className="uzenet bad">{hiba}</div>}

      {adat.eltero.length === 0 ? (
        <div style={{ marginTop: 8 }}>
          Ebben a mentésben ugyanaz áll, mint most. Próbálj egy korábbi mentést.
        </div>
      ) : (
        <div className="matrix" style={{ maxHeight: 420, overflow: 'auto', marginTop: 8 }}>
          <table className="tt2">
            <thead>
              <tr>
                <th style={{ width: 34 }} />
                <th>Partner most</th>
                <th>Mező</th>
                <th>Most ez áll</th>
                <th>A mentésben ez állt</th>
              </tr>
            </thead>
            <tbody>
              {adat.eltero.flatMap((s) =>
                s.mezok.map((m) => {
                  const kulcs = `${s.etterem_id}:${m.mezo}`;
                  return (
                    <tr key={kulcs} className={m.mezo === 'nev' ? 'kijelolt' : undefined}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={kijelolt.has(kulcs)}
                          onChange={() => valt(kulcs)}
                        />
                      </td>
                      <td className="nm3">
                        <b>{s.nev_most}</b>
                      </td>
                      <td>{m.cimke}</td>
                      <td>{m.most ?? <span style={{ color: 'var(--ghost)' }}>üres</span>}</td>
                      <td>
                        <b>{m.mentesben ?? <span style={{ color: 'var(--ghost)' }}>üres</span>}</b>
                      </td>
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="btns" style={{ marginTop: 8 }}>
        <button
          className="btn gold"
          disabled={munkaban || kijelolt.size === 0}
          onClick={() => void visszair()}
        >
          {munkaban ? 'Visszaírás…' : `Kijelöltek visszaírása (${ft(kijelolt.size)})`}
        </button>
        <button className="btn sm" onClick={onMegse}>
          Mégsem
        </button>
      </div>
      <div className="hint" style={{ marginTop: 4 }}>
        Alapból a <b>névcserék</b> vannak kijelölve — azokért szokás ide nyúlni. Minden visszaírt
        partner naplósort kap, tehát visszakövethető.
      </div>
    </div>
  );
}
